import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Responsive, WidthProvider } from "react-grid-layout";
import API from "../Api";
import WidgetRenderer from "./WidgetRenderer";
import { useLogout } from "../hooks/useLogout";
import { useDashboardTimeRange } from "../hooks/useDashboardTimeRange";
import DashboardTimeRangeControls from "./DashboardTimeRangeControls";
import "./Dashboard.css";
import UserTimezoneSelect from "./UserTimezoneSelect";
import AskCommrayAi from "./AskCommrayAi";
import { exportDashboardGridPng, exportWidgetImagesFromRoot } from "../utils/dashboardCapture";
import { DashboardChartSyncProvider } from "../context/DashboardChartSyncContext";

const ResponsiveGridLayout = WidthProvider(Responsive);

function DashboardViewer() {
  const { dashboardName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const handleLogout = useLogout();
  const [dashboard, setDashboard] = useState(null);
  const [userInfo, setUserInfo] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedDashboards, setSavedDashboards] = useState([]);
  const [savedDashboardsOpen, setSavedDashboardsOpen] = useState(false);
  const [timeRangeOpen, setTimeRangeOpen] = useState(true);
  const {
    selection: timeSelection,
    updateSelection: updateTimeSelection,
    startTime,
    endTime,
    refreshTrigger,
    clear: clearDashboardTime,
    refreshNow
  } = useDashboardTimeRange(location.state);
  const [captureBusy, setCaptureBusy] = useState(false);
  const dashboardGridRef = useRef(null);

  const handlePrintPage = useCallback(() => {
    window.print();
  }, []);

  const handleExportDashboardPng = useCallback(async () => {
    if (!dashboard?.name) return;
    try {
      setCaptureBusy(true);
      await exportDashboardGridPng(dashboardGridRef.current, dashboard.name);
    } catch (e) {
      console.error(e);
      alert(e?.message || "Could not export dashboard image.");
    } finally {
      setCaptureBusy(false);
    }
  }, [dashboard?.name]);

  const handleExportWidgetPngs = useCallback(async () => {
    if (!dashboard?.name) return;
    try {
      setCaptureBusy(true);
      await exportWidgetImagesFromRoot(dashboardGridRef.current, dashboard.name);
    } catch (e) {
      console.error(e);
      alert(e?.message || "Could not export widget images.");
    } finally {
      setCaptureBusy(false);
    }
  }, [dashboard?.name]);

  useEffect(() => {
  const fetchDashboard = async () => {
    try {
      setLoading(true);
      // First, get all dashboards to find the one with matching name
      const dashboardsRes = await API.get('/api/dashboard');
      const dashboards = dashboardsRes.data || [];
      const dashboard = dashboards.find(d => d.name === dashboardName);
      
      if (dashboard) {
        // Use the dashboard ID to fetch the full dashboard data
        const res = await API.get(`/api/dashboard/${dashboard.id}`);
        const dashboardData = res.data;
        
        // Parse the dashboard configuration to extract widgets and layouts
        try {
          console.log('Raw dashboard data:', dashboardData);
          console.log('Dashboard config string:', dashboardData.dashboardConfig);
          
          const config = JSON.parse(dashboardData.dashboardConfig);
          console.log('Parsed config:', config);
          console.log('Widgets from config:', config.widgets);
          
          // Normalize widgets: convert old format to new format
          const normalizedWidgets = (config.widgets || []).map(widget => {
            // If widget has a config object, flatten it
            if (widget.config) {
              widget = { ...widget, ...widget.config };
              delete widget.config;
            }
            
            // Normalize widget type
            if (widget.type === 'chart') {
              // Convert 'chart' type to 'line-chart' or 'bar-chart' based on chartType
              widget.type = widget.chartType === 'bar' ? 'bar-chart' : 'line-chart';
            } else if (widget.type === 'metric') {
              widget.type = 'single-metric';
            }
            
            // Chart widgets default to 'logs' so they show same data as Log Explorer
            const title = (widget.title || '').toLowerCase();
            const isChartWidget = ['line-chart', 'area-chart', 'bar-chart', 'horizontal-bar-chart', 'pie-chart', 'donut-chart', 'log-table', 'custom-table', 'chart'].includes(widget.type) || widget.chartType;
            if (title.includes('transaction') || title.includes('transactions')) {
              widget.dataSource = 'logs';
            } else if (!widget.dataSource) {
              widget.dataSource = isChartWidget ? 'logs' : 'metrics';
            }
            console.log(`Widget "${widget.title}": dataSource=${widget.dataSource}, type=${widget.type}`);
            
            // Ensure widget has required properties
            // If useCustomEndpoint is true but customEndpoint is empty/invalid, disable it
            const hasValidCustomEndpoint = widget.useCustomEndpoint && 
                                          widget.customEndpoint && 
                                          widget.customEndpoint.trim() !== '';
            
            return {
              ...widget,
              id: widget.id || `widget-${Date.now()}-${Math.random()}`,
              type: widget.type || 'line-chart',
              title: widget.title || 'Untitled Widget',
              dataSource:
                widget.type === 'custom-table' ? 'logs' : widget.dataSource || 'metrics',
              metric: widget.metric || '',
              service: widget.service || '',
              logLevel: widget.logLevel || '',
              theme: widget.theme || 'blue',
              chartColor: widget.chartColor || 'blue',
              groupBy: widget.groupBy || 'none',
              keyword: widget.keyword || '',
              logql: widget.logql || '',
              timeRange: widget.timeRange || '24h',
              // Always preserve multi-series config from saved JSON (must stay an array)
              datasets: Array.isArray(widget.datasets) ? widget.datasets : [],
              customTableColumns: Array.isArray(widget.customTableColumns) ? widget.customTableColumns : [],
              useCustomEndpoint: hasValidCustomEndpoint,
              customEndpoint: widget.customEndpoint || ''
            };
          });
          
          // Generate layout from widgets when layouts is missing (sample dashboards have x,y,w,h on widgets but no layouts)
          const rawLayouts = config.layouts || { lg: [], md: [], sm: [], xs: [] };
          const hasLayoutItems = rawLayouts.lg?.length > 0 || rawLayouts.md?.length > 0;
          const layouts = hasLayoutItems ? rawLayouts : (() => {
            const items = normalizedWidgets.map((w) => ({
              i: String(w.id),
              x: w.x ?? 0,
              y: w.y ?? 0,
              w: w.w ?? 6,
              h: w.h ?? 4
            }));
            return { lg: items, md: items, sm: items, xs: items };
          })();

          const dashboardWithConfig = {
            ...dashboardData,
            name: config.name || dashboardData.name,
            description: config.description || dashboardData.description,
            widgets: normalizedWidgets,
            layouts
          };
          
          console.log('Final dashboard with config:', dashboardWithConfig);
          console.log('Normalized widgets:', normalizedWidgets);
          setDashboard(dashboardWithConfig);
        } catch (parseError) {
          console.error('Failed to parse dashboard config:', parseError);
          console.error('Raw config string:', dashboardData.dashboardConfig);
          // Fallback to basic dashboard data
          setDashboard({
            ...dashboardData,
            widgets: [],
            layouts: { lg: [], md: [], sm: [], xs: [] }
          });
        }
        setError(null);
      } else {
        setError(`Dashboard "${dashboardName}" not found.`);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
      setError("Failed to load dashboard. It may not exist or you may not have permission to view it.");
    } finally {
      setLoading(false);
    }
  };

    const fetchUserInfo = async () => {
      try {
        const res = await API.get("/auth/user-info");
        
        if (res.data.username) {
          setUserInfo({
            username: res.data.username,
            role: res.data.role || "USER"
          });
          
          const userTheme = res.data.themePreference === "dark";
          setDarkMode(userTheme);
        } else {
          setUserInfo({
            username: localStorage.getItem("username") || "User",
            role: localStorage.getItem("userRole") || "USER"
          });
        }
      } catch (error) {
        console.error("Failed to fetch user info:", error);
        setUserInfo({
          username: localStorage.getItem("username") || "User",
          role: localStorage.getItem("userRole") || "USER"
        });
      }
    };

    const fetchDashboards = async () => {
      try {
        const response = await API.get('/api/dashboard');
        setSavedDashboards(response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to fetch dashboards:', error);
        return [];
      }
    };
    
    if (dashboardName) {
      fetchDashboard();
    }
    fetchUserInfo();
    fetchDashboards();
  }, [dashboardName]);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
    setSettingsOpen(false);
  };

  const toggleSettings = () => {
    setSettingsOpen(!settingsOpen);
    setDropdownOpen(false);
  };

  const handleProfileClick = () => {
    setDropdownOpen(false);
    navigate("/settings/profile");
  };

  const handleChangePasswordClick = () => {
    setDropdownOpen(false);
    navigate("/change-password");
  };

  const toggleDarkMode = async () => {
    const newDarkMode = !darkMode;
    const theme = newDarkMode ? 'dark' : 'light';
    
    try {
      const response = await API.post('/auth/update-theme', {
        theme: theme
      });

      if (response.data.message) {
        setDarkMode(newDarkMode);
        setSettingsOpen(false);
        console.log('Theme preference updated successfully');
      } else {
        console.error('Failed to update theme preference');
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
      setDarkMode(newDarkMode);
      localStorage.setItem('darkMode', newDarkMode.toString());
      setSettingsOpen(false);
    }
  };

  const handleEditDashboard = () => {
    navigate(`/dashboards/${encodeURIComponent(dashboardName)}/dashboard-builder`);
  };

  const handleBackToDashboards = () => {
    navigate('/dashboards');
  };

  const handleDashboardSelect = (selectedDashboard) => {
    navigate(`/dashboards/${encodeURIComponent(selectedDashboard.name)}/dashboard-viewer`);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.user-dropdown')) {
        setDropdownOpen(false);
      }
      if (settingsOpen && !event.target.closest('.settings-dropdown')) {
        setSettingsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, settingsOpen]);

  if (loading) {
    return (
      <div className={`dashboard-container ${darkMode ? 'dark-mode' : 'light-mode'}`}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px' 
        }}>
          Loading dashboard...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`dashboard-container ${darkMode ? 'dark-mode' : 'light-mode'}`}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={handleBackToDashboards} className="action-button primary">
            Back to Dashboards
          </button>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className={`dashboard-container ${darkMode ? 'dark-mode' : 'light-mode'}`}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <h2>Dashboard Not Found</h2>
          <p>The dashboard "{dashboardName}" could not be found.</p>
          <button onClick={handleBackToDashboards} className="action-button primary">
            Back to Dashboards
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard-viewer ${darkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* Blue Header Stripe */}
      <header className="dashboard-header commray-no-print">
        <div className="header-content">
          {/* Commray Branding - Left Side */}
          <div className="brand-section">
            <h1 className="brand-name">Commray</h1>
          </div>

          {/* Dashboard Title - Center */}
          <div className="center-title">
            <h2 className="dashboard-title-header">{dashboard.name}</h2>
            <span className="dashboard-subtitle">Viewer Mode</span>
          </div>

          {/* Right Side Controls */}
          <div className="right-controls">
            <AskCommrayAi />
            {/* Edit Button */}
            <button 
              className="edit-button"
              onClick={handleEditDashboard}
              title="Edit Dashboard"
            >
              ✏️ Edit
            </button>

            {/* Back Button */}
            <button 
              className="back-button"
              onClick={handleBackToDashboards}
              title="Back to Dashboards"
            >
              ← Back
            </button>

            {/* Settings Button */}
            <div className="settings-dropdown">
              <button className="settings-button" onClick={toggleSettings}>
                <span className="settings-icon">⚙️</span>
              </button>

              {settingsOpen && (
                <div className="settings-menu">
                  <div className="settings-header">
                    <h4>Settings</h4>
                  </div>
                  <div className="settings-divider"></div>
                  <div className="settings-item" onClick={toggleDarkMode}>
                    <span className="settings-item-icon">{darkMode ? '☀️' : '🌙'}</span>
                    <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
                  </div>
                  <UserTimezoneSelect />
                  <div className="settings-divider" />
                  <div
                    className={`settings-item${captureBusy || !dashboard?.widgets?.length ? ' settings-item--disabled' : ''}`}
                    onClick={() => {
                      if (captureBusy || !dashboard?.widgets?.length) return;
                      setSettingsOpen(false);
                      handlePrintPage();
                    }}
                    role="menuitem"
                  >
                    <span className="settings-item-icon">🖨</span>
                    <span>Print page</span>
                  </div>
                  <div
                    className={`settings-item${captureBusy || !dashboard?.widgets?.length ? ' settings-item--disabled' : ''}`}
                    onClick={() => {
                      if (captureBusy || !dashboard?.widgets?.length) return;
                      setSettingsOpen(false);
                      void handleExportDashboardPng();
                    }}
                    role="menuitem"
                  >
                    <span className="settings-item-icon">📷</span>
                    <span>Export dashboard as PNG</span>
                  </div>
                  <div
                    className={`settings-item${captureBusy || !dashboard?.widgets?.length ? ' settings-item--disabled' : ''}`}
                    onClick={() => {
                      if (captureBusy || !dashboard?.widgets?.length) return;
                      setSettingsOpen(false);
                      void handleExportWidgetPngs();
                    }}
                    role="menuitem"
                  >
                    <span className="settings-item-icon">📷</span>
                    <span>Export widgets as PNG</span>
                  </div>
                </div>
              )}
            </div>

            {/* User Dropdown */}
            <div className="user-dropdown">
              <button className="user-button" onClick={toggleDropdown}>
                <div className="user-avatar">
                  {userInfo.username ? userInfo.username.charAt(0).toUpperCase() : 'U'}
                </div>
                <div className="user-info">
                  <span className="user-name">{userInfo.username}</span>
                  <span className="user-role">{userInfo.role?.replace('ROLE_', '')}</span>
                </div>
                <div className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`}>
                  ▼
                </div>
              </button>

              {dropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-header">
                    <div className="dropdown-user-info">
                      <div className="dropdown-avatar">
                        {userInfo.username ? userInfo.username.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div>
                        <div className="dropdown-name">{userInfo.username}</div>
                        <div className="dropdown-role">{userInfo.role?.replace('ROLE_', '')}</div>
                      </div>
                    </div>
                  </div>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-item" onClick={handleProfileClick}>
                    <span className="dropdown-icon">👤</span>
                    Settings
                  </div>
                  <div className="dropdown-item" onClick={handleChangePasswordClick}>
                    <span className="dropdown-icon">🔒</span>
                    Change Password
                  </div>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-item logout-item" onClick={handleLogout}>
                    <span className="dropdown-icon">🚪</span>
                    Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Left Sidebar - Dashboard Management */}
      <div className="dashboard-sidebar commray-no-print">
        <div className="sidebar-header">
          <h3>Dashboard Management</h3>
        </div>
        <div className="sidebar-content">
          {/* Time Range Section */}
          <div className="sidebar-section time-range-section">
            <div className="sidebar-dropdown-header" onClick={() => setTimeRangeOpen(!timeRangeOpen)}>
              <h4>⏱️ Time Range</h4>
              <span className={`dropdown-arrow ${timeRangeOpen ? 'open' : ''}`}>▼</span>
            </div>
            {timeRangeOpen && (
              <div className="sidebar-dropdown-content">
                <DashboardTimeRangeControls
                  selection={timeSelection}
                  onChange={updateTimeSelection}
                />
                <button
                  type="button"
                  className="sidebar-btn secondary"
                  style={{ marginTop: "10px", width: "100%" }}
                  onClick={clearDashboardTime}
                >
                  Clear All Filters
                </button>
                <button
                  type="button"
                  className="sidebar-btn secondary"
                  style={{ marginTop: "8px", width: "100%" }}
                  onClick={refreshNow}
                >
                  🔄 Refresh Data
                </button>
              </div>
            )}
          </div>

          {/* Saved Dashboards Section */}
          <div className="sidebar-section">
            <div className="sidebar-dropdown-header" onClick={() => setSavedDashboardsOpen(!savedDashboardsOpen)}>
              <h4>Saved Dashboards</h4>
              <span className={`dropdown-arrow ${savedDashboardsOpen ? 'open' : ''}`}>▼</span>
            </div>
            {savedDashboardsOpen && (
              <div className="sidebar-dropdown-content">
                {savedDashboards.map((savedDashboard) => (
                  <div
                    key={savedDashboard.id}
                    className={`sidebar-dashboard-item ${dashboard?.name === savedDashboard.name ? 'active' : ''}`}
                    onClick={() => handleDashboardSelect(savedDashboard)}
                  >
                    <div className="dashboard-item-name">{savedDashboard.name}</div>
                    <div className="dashboard-item-actions">
                      <button
                        className="sidebar-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dashboards/${encodeURIComponent(savedDashboard.name)}/dashboard-builder`);
                        }}
                        title="Edit Dashboard"
                      >
                        ✏️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Dashboard Content */}
      <main className="dashboard-content">

        {/* Dashboard Grid */}
        <div ref={dashboardGridRef} className="dashboard-grid">
          {dashboard.widgets && dashboard.widgets.length > 0 ? (
            <DashboardChartSyncProvider dashboardKey={dashboard.id || dashboard.name || dashboardName}>
              <ResponsiveGridLayout
                className="layout"
                layouts={dashboard.layouts || { lg: [], md: [], sm: [], xs: [] }}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                rowHeight={60}
                isDraggable={false} // Viewer mode, not draggable
                isResizable={false} // Viewer mode, not resizable
                margin={[8, 8]}
                containerPadding={[4, 4]}
              >
                {dashboard.widgets.map((widget) => {
                  if (!widget || !widget.id) {
                    console.warn('Invalid widget found:', widget);
                    return null;
                  }
                  return (
                    <div key={widget.id} className={`widget-container commray-widget-capture theme-${widget.theme || 'blue'}`}>
                      <div className="widget-header">
                        <div className="widget-title-right">
                          <h3>{widget.title || 'Untitled Widget'}</h3>
                        </div>
                      </div>
                      <div className="widget-content">
                        <WidgetRenderer widget={widget} startTime={startTime} endTime={endTime} refreshTrigger={refreshTrigger} />
                      </div>
                    </div>
                  );
                })}
              </ResponsiveGridLayout>
            </DashboardChartSyncProvider>
          ) : (
            <div className="no-widgets">
              <h3>No widgets configured</h3>
              <p>This dashboard doesn't have any widgets yet.</p>
              <button 
                className="action-button primary"
                onClick={handleEditDashboard}
              >
                Add Widgets
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default DashboardViewer;
